import React, { useState, useEffect } from 'react'
import Card from '../../components/Card.jsx'
import TipBox from '../../components/TipBox.jsx'
import FilterBar from '../../components/FilterBar.jsx'
import * as api from '../../services/api.js'
import AppSelect from '../../components/AppSelect.jsx'

const GOAL_TYPES = [
  { key: 'all',       label: 'All types',  icon: '✦' },
  { key: 'Language',  label: 'Language',   icon: '🗣️' },
  { key: 'Creative',  label: 'Creative',   icon: '🎨' },
  { key: 'Fitness',   label: 'Fitness',    icon: '🏃' },
  { key: 'Reading',   label: 'Reading',    icon: '📖' },
  { key: 'Writing',   label: 'Writing',    icon: '✍️' },
  { key: 'Music',     label: 'Music',      icon: '🎵' },
  { key: 'Tech',      label: 'Tech',       icon: '💻' },
  { key: 'Wellbeing', label: 'Wellbeing',  icon: '🌿' },
  { key: 'Other',     label: 'Other',      icon: '⭐' },
]

const TYPE_COLOUR = {
  Language:  '#2E6DB4',
  Music:     '#7B4EAB',
  Tech:      '#238b45',
  Sport:     '#c4963a',
  Art:       '#e05c8a',
  Reading:   '#2E7D3E',
  Creative:  '#b5651d',
  Fitness:   '#c4963a',
  Writing:   '#5a7a9a',
  Wellbeing: '#52A96A',
  Other:     '#8FBD8F',
}

function goalProgress(g) {
  if (!g.tasks?.length) return g.progress || 0
  return Math.round(g.tasks.filter(t => t.done).length / g.tasks.length * 100)
}

function daysLeft(deadline) {
  if (!deadline) return null
  const diff = Math.ceil((new Date(deadline) - new Date()) / 86400000)
  if (diff < 0)   return { label: 'Overdue',   colour: '#c0392b' }
  if (diff === 0) return { label: 'Due today',  colour: '#c4963a' }
  return { label: `${diff}d left`, colour: 'var(--stone)' }
}

export default function Goals() {
  const [goals,        setGoals]        = useState([])
  const [statusFilter, setStatusFilter] = useState('active')
  const [typeFilter,   setTypeFilter]   = useState('all')
  const [taskDrafts,   setTaskDrafts]   = useState({})
  const [draft,        setDraft]        = useState({ title: '', category: 'Other', deadline: '' })

  useEffect(() => { api.getGoals().then(rows => setGoals(rows || [])) }, [])

  function updateGoals(updater) {
    setGoals(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      api.saveGoals(next)
      return next
    })
  }

  function addGoal() {
    if (!draft.title.trim()) return
    updateGoals(prev => [{
      id:       Date.now(),
      title:    draft.title.trim(),
      category: draft.category,
      deadline: draft.deadline,
      tasks:    [],
      progress: 0,
      status:   'active',
    }, ...prev])
    setDraft(d => ({ ...d, title: '', deadline: '' }))
  }

  function addTask(goalId, text) {
    if (!text?.trim()) return
    updateGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g
      const tasks    = [...(g.tasks || []), { id: Date.now(), text: text.trim(), done: false }]
      const progress = Math.round(tasks.filter(t => t.done).length / tasks.length * 100)
      return { ...g, tasks, progress }
    }))
    setTaskDrafts(d => ({ ...d, [goalId]: '' }))
  }

  function toggleTask(goalId, taskId) {
    updateGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g
      const tasks    = (g.tasks || []).map(t => t.id === taskId ? { ...t, done: !t.done } : t)
      const progress = tasks.length ? Math.round(tasks.filter(t => t.done).length / tasks.length * 100) : 0
      const status   = progress >= 100 ? 'done' : 'active'
      return { ...g, tasks, progress, status }
    }))
  }

  function removeTask(goalId, taskId) {
    updateGoals(prev => prev.map(g => {
      if (g.id !== goalId) return g
      const tasks    = (g.tasks || []).filter(t => t.id !== taskId)
      const progress = tasks.length ? Math.round(tasks.filter(t => t.done).length / tasks.length * 100) : 0
      return { ...g, tasks, progress }
    }))
  }

  function markDone(id) {
    updateGoals(prev => prev.map(g => {
      if (g.id !== id) return g
      const tasks = (g.tasks || []).map(t => ({ ...t, done: true }))
      return { ...g, tasks, progress: 100, status: 'done' }
    }))
  }

  function reopen(id) {
    updateGoals(prev => prev.map(g =>
      g.id === id ? { ...g, status: 'active' } : g
    ))
  }

  function remove(id) {
    updateGoals(prev => prev.filter(g => g.id !== id))
  }

  const activeCount = goals.filter(g => g.status === 'active').length
  const doneCount   = goals.filter(g => g.status === 'done').length
  const avgProgress = goals.length
    ? Math.round(goals.reduce((s, g) => s + goalProgress(g), 0) / goals.length)
    : 0

  const visible = goals.filter(g => {
    const statusOk = statusFilter === 'all'    ? true
                   : statusFilter === 'active' ? g.status === 'active'
                   : g.status === 'done'
    const typeOk   = typeFilter === 'all' ? true : g.category === typeFilter
    return statusOk && typeOk
  })

  return (
    <div className="page active">
      <div className="page-inner">

        <div className="hero">
          <p className="hero-eyebrow">Growth</p>
          <h1>My <em>Goals</em></h1>
          <hr className="hero-rule" />
        </div>

        {/* Stats */}
        {goals.length > 0 && (
          <div className="mini-stat-row">
            <div className="mini-stat">
              <div className="mini-stat-label">Total</div>
              <div className="mini-stat-value">{goals.length}</div>
            </div>
            <div className="mini-stat">
              <div className="mini-stat-label">In progress</div>
              <div className="mini-stat-value">{activeCount}</div>
            </div>
            <div className="mini-stat">
              <div className="mini-stat-label">Completed</div>
              <div className="mini-stat-value">{doneCount}</div>
            </div>
            <div className="mini-stat">
              <div className="mini-stat-label">Avg progress</div>
              <div className="mini-stat-value">{avgProgress}%</div>
            </div>
          </div>
        )}

        {/* Add goal */}
        <Card label="Growth" title="Set a new goal">
          <div className="cook-form">
            <div className="full">
              <label className="field-label">What do you want to achieve?</label>
              <input
                className="field-input"
                value={draft.title}
                onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && addGoal()}
                placeholder="e.g. Learn conversational Spanish, finish an oil painting…"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="field-label">Type</label>
              <AppSelect
                value={draft.category}
                onChange={v => setDraft(d => ({ ...d, category: v }))}
                options={GOAL_TYPES.filter(t => t.key !== 'all').map(t => ({ value: t.key, label: t.label }))}
                placeholder="Type…"
              />
            </div>
            <div>
              <label className="field-label">Target date (optional)</label>
              <input
                className="field-input"
                type="date"
                value={draft.deadline}
                onChange={e => setDraft(d => ({ ...d, deadline: e.target.value }))}
              />
            </div>
          </div>
          <button
            className={`save-btn ${draft.title.trim() ? 'ready' : ''}`}
            style={{ marginTop: '0.75rem' }}
            onClick={addGoal}
          >
            Add Goal
          </button>
        </Card>

        {/* Filters */}
        {goals.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '6px' }}>
              {[
                { id: 'active', label: `In progress (${activeCount})` },
                { id: 'done',   label: `Completed (${doneCount})` },
                { id: 'all',    label: 'All' },
              ].map(f => (
                <button
                  key={f.id}
                  className={`archetype-chip ${statusFilter === f.id ? 'active' : ''}`}
                  onClick={() => setStatusFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <FilterBar
              filters={GOAL_TYPES}
              active={typeFilter}
              onChange={setTypeFilter}
            />
          </div>
        )}

        {/* Goal list */}
        {goals.length === 0 ? (
          <Card label="" title="">
            <p style={{ color: 'var(--stone)', fontStyle: 'italic', fontSize: '0.88rem' }}>
              No goals yet — add your first one above.
            </p>
          </Card>
        ) : visible.length === 0 ? (
          <Card label="" title="">
            <p style={{ color: 'var(--stone)', fontStyle: 'italic', fontSize: '0.88rem' }}>
              Nothing matches the current filters.
            </p>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '1.5rem' }}>
            {visible.map(goal => {
              const dl       = daysLeft(goal.deadline)
              const color    = TYPE_COLOUR[goal.category] || 'var(--stone)'
              const done     = goal.status === 'done'
              const progress = goalProgress(goal)
              const tasks    = goal.tasks || []
              const tasksDone = tasks.filter(t => t.done).length

              return (
                <div key={goal.id} className="card" style={{ margin: 0, padding: '1.1rem 1.25rem' }}>

                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '0.75rem' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: done ? 'var(--accent)' : color, flexShrink: 0, marginTop: 5 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{
                        fontFamily: 'var(--font-serif)', fontSize: '1rem', color: 'var(--ink)',
                        textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.65 : 1,
                      }}>
                        {goal.title}
                      </span>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.68rem', borderRadius: '20px', padding: '2px 8px', background: color + '22', color }}>
                          {goal.category}
                        </span>
                        {dl && <span style={{ fontSize: '0.72rem', color: dl.colour }}>{dl.label}</span>}
                        {tasks.length > 0 && (
                          <span style={{ fontSize: '0.72rem', color: 'var(--stone)' }}>
                            {tasksDone}/{tasks.length} steps
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      className="book-delete"
                      style={{ opacity: 1, fontSize: '11px', flexShrink: 0 }}
                      onClick={() => remove(goal.id)}
                    >✕</button>
                  </div>

                  {/* Progress bar */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span style={{ fontSize: '0.7rem', color: 'var(--stone)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Progress</span>
                      <span style={{ fontSize: '0.75rem', color, fontWeight: 600 }}>
                        {tasks.length ? `${progress}%` : 'Add steps below'}
                      </span>
                    </div>
                    <div className="w-progress-bar-wrap" style={{ margin: 0 }}>
                      <div
                        className="w-progress-bar"
                        style={{ width: `${progress}%`, background: done ? 'var(--accent)' : color }}
                      />
                    </div>
                  </div>

                  {/* Tasks checklist */}
                  <div className="goal-tasks">
                    {tasks.map(t => (
                      <div key={t.id} className="goal-task-row">
                        <input
                          type="checkbox"
                          checked={t.done}
                          onChange={() => toggleTask(goal.id, t.id)}
                          className="goal-task-check"
                        />
                        <span className={`goal-task-text${t.done ? ' done' : ''}`}>{t.text}</span>
                        <button
                          className="goal-task-del"
                          onClick={() => removeTask(goal.id, t.id)}
                        >✕</button>
                      </div>
                    ))}
                    {!done && (
                      <div className="goal-task-add-row">
                        <input
                          className="goal-task-input"
                          placeholder="Add a step…"
                          value={taskDrafts[goal.id] || ''}
                          onChange={e => setTaskDrafts(d => ({ ...d, [goal.id]: e.target.value }))}
                          onKeyDown={e => {
                            if (e.key === 'Enter') addTask(goal.id, taskDrafts[goal.id])
                          }}
                        />
                        <button
                          className="ghost-btn"
                          style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                          onClick={() => addTask(goal.id, taskDrafts[goal.id])}
                        >+</button>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '0.75rem' }}>
                    {done ? (
                      <>
                        <span style={{ fontSize: '0.82rem', color: 'var(--accent)', fontWeight: 500 }}>✓ Completed</span>
                        <button className="ghost-btn" style={{ fontSize: '0.75rem', marginLeft: 'auto' }} onClick={() => reopen(goal.id)}>
                          Reopen
                        </button>
                      </>
                    ) : (
                      <button
                        className="save-btn ready"
                        style={{ fontSize: '0.75rem', padding: '5px 14px', marginLeft: 'auto' }}
                        onClick={() => markDone(goal.id)}
                      >
                        Mark complete ✓
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Goal ideas */}
        <Card label="Growth" title="Goal ideas">
          <TipBox title="Language & communication">
            <ul>
              <li>Reach conversational level in a new language (3–6 months of 20 min/day)</li>
              <li>Read one non-fiction book per month for a year</li>
              <li>Write in a journal every day for 30 days</li>
            </ul>
          </TipBox>
          <TipBox title="Creative & artistic">
            <ul>
              <li>Complete a painting, drawing, or craft project from start to finish</li>
              <li>Learn five songs on a musical instrument</li>
              <li>Write and finish a short story or personal essay</li>
            </ul>
          </TipBox>
          <TipBox title="Physical & wellbeing">
            <ul>
              <li>Walk 7,000 steps a day for 30 consecutive days</li>
              <li>Learn to cook 10 new recipes from scratch</li>
              <li>Establish a consistent morning or evening routine and keep it for 60 days</li>
            </ul>
          </TipBox>
          <TipBox title="Skills & knowledge">
            <ul>
              <li>Complete an online course in something you've always been curious about</li>
              <li>Learn the basics of a new software tool or technical skill</li>
              <li>Research and plan a trip you'd genuinely love to take</li>
            </ul>
          </TipBox>
        </Card>

      </div>
    </div>
  )
}
